/**
 * 005-packing-verification, admin-packing-oversight bucket. T057 — `GET /oversight/collisions`
 * (FR-034, D-29, SC-007): SKU pairs whose expected-weight tolerance bands overlap, and the SKUs
 * that overlap forces into confirmation. Same underlying computation as `../reports/Collisions.tsx`
 * (`008`'s analytical envelope around the identical `pairs`/`excluded` shapes, research R4) — this
 * screen reuses that file's own column sets verbatim rather than redrawing the same two tables,
 * since `CollisionPair`/`CollisionCandidate` are the exact same generated schemas either route
 * returns.
 */
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Alert } from "rizzui";
import { ErrorSurface, EmptyState } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { can } from "../../lib/permissions";
import { getOversightCollisions, type OversightCollisionReport, type CollisionPair, type CollisionCandidate } from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";
import { collisionPairColumns, collisionExcludedColumns } from "../reports/collisions-columns";

/** The whole report in one response: `PagedTable`'s whole-set mode, paged in memory. */
const whole = <T,>(items: T[]) => ({ items, page: 1, pageSize: items.length, totalCount: items.length });

export default function PackingCollisionsPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [report, setReport] = useState<OversightCollisionReport | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    (async () => {
      try {
        setReport(await getOversightCollisions());
      } catch (err) {
        setError(err);
      }
    })();
  }, []);

  if (!can(permissionState, "packing.oversight")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "The weight-collision report is gated by packing.oversight, which your role does not hold." }}
        action="view the weight-collision report"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Weight Collisions"
        count={report ? report.pairs.length : null}
        description="SKU pairs whose expected-weight tolerance bands overlap closely enough that a scale reading alone cannot tell them apart. Recomputed on every read — a pair that stops overlapping stops appearing and stops forcing confirmation."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Packing" }, { name: "Collisions" }]}
      />

      {error ? <ErrorSurface error={error} action="load the weight-collision report" /> : null}

      {report ? (
        report.pairs.length === 0 ? (
          <EmptyState title="No colliding SKU pairs">No two SKUs this service holds work for currently overlap on expected weight.</EmptyState>
        ) : (
          <>
            <Alert color="danger" bar>
              Both SKUs in every listed pair are flagged to force explicit confirmation at packing, whatever the reading.
            </Alert>

            <PagedTable<CollisionPair>
              result={whole(report.pairs)}
              caption="Overlapping SKU pairs, with the overlap extent"
              columns={collisionPairColumns}
              title="Overlapping pairs"
              empty={null}
            />

            {report.excluded.length > 0 ? (
              <PagedTable<CollisionCandidate>
                result={whole(report.excluded)}
                caption="SKUs excluded from the collision check for want of an expected weight"
                columns={collisionExcludedColumns}
                title={<>Could not be checked · {report.excluded.length}</>}
                description="These SKUs have no expected weight to compare — listed, not treated as collision-free."
                empty={null}
              />
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
}
